import React, { useState, useEffect } from 'react';
import { bookingTemplateAPI, propertyAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FileText, Plus, Edit, Trash2, Eye, GripVertical, Upload,
  X, Check, Settings, PenTool, RefreshCw
} from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: 'Aa' },
  { value: 'number', label: 'Number', icon: '#' },
  { value: 'email', label: 'Email', icon: '@' },
  { value: 'phone', label: 'Phone', icon: 'Ph' },
  { value: 'date', label: 'Date', icon: 'D' },
  { value: 'select', label: 'Dropdown', icon: 'v' },
  { value: 'textarea', label: 'Long Text', icon: 'T' },
  { value: 'currency', label: 'Currency', icon: '$' },
  { value: 'ic_number', label: 'IC Number', icon: 'ID' },
  { value: 'address', label: 'Address', icon: 'A' },
  { value: 'signature', label: 'Signature', icon: 'S' },
];

const SECTIONS = [
  'Buyer Information',
  'Property Details',
  'Financing',
  'Legal',
  'Additional',
];

const AdminBookingTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    developer_id: '',
    project_id: '',
    form_fields: [],
    signature_positions: [],
  });

  // New field state
  const [newField, setNewField] = useState({
    name: '',
    label: '',
    field_type: 'text',
    required: false,
    section: 'Buyer Information',
    options: '',
    placeholder: '',
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await bookingTemplateAPI.getTemplates();
      setTemplates(res.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!formData.name) {
      toast.error('Template name is required');
      return;
    }

    try {
      await bookingTemplateAPI.createTemplate(formData);
      toast.success('Template created successfully');
      setShowCreateModal(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      await bookingTemplateAPI.updateTemplate(selectedTemplate.id, formData);
      toast.success('Template updated successfully');
      setShowEditModal(false);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await bookingTemplateAPI.deleteTemplate(id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleUploadPdf = async (templateId, file) => {
    try {
      await bookingTemplateAPI.uploadPdf(templateId, file);
      toast.success('PDF template uploaded');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to upload PDF');
    }
  };

  const openEditModal = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      developer_id: template.developer_id || '',
      project_id: template.project_id || '',
      form_fields: template.form_fields || [],
      signature_positions: template.signature_positions || [],
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      developer_id: '',
      project_id: '',
      form_fields: [],
      signature_positions: [],
    });
  };

  const addFormField = () => {
    if (!newField.name || !newField.label) {
      toast.error('Field name and label are required');
      return;
    }

    const field = {
      id: `field_${Date.now()}`,
      ...newField,
      options: newField.options ? newField.options.split(',').map(o => o.trim()) : [],
      order: formData.form_fields.length + 1,
    };

    setFormData({
      ...formData,
      form_fields: [...formData.form_fields, field],
    });

    setNewField({
      name: '',
      label: '',
      field_type: 'text',
      required: false,
      section: 'Buyer Information',
      options: '',
      placeholder: '',
    });
  };

  const removeFormField = (fieldId) => {
    setFormData({
      ...formData,
      form_fields: formData.form_fields.filter(f => f.id !== fieldId),
    });
  };

  const addSignaturePosition = () => {
    const newSig = {
      role: 'customer',
      label: 'Signature',
      page: 1,
      x: 100,
      y: 600,
      width: 200,
      height: 60,
      required: true,
    };

    setFormData({
      ...formData,
      signature_positions: [...formData.signature_positions, newSig],
    });
  };

  const updateSignaturePosition = (index, field, value) => {
    const updated = [...formData.signature_positions];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, signature_positions: updated });
  };

  const removeSignaturePosition = (index) => {
    setFormData({
      ...formData,
      signature_positions: formData.signature_positions.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="booking-templates-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking Form Templates</h1>
          <p className="text-gray-500">Create and manage dynamic booking forms for different developers</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">No templates yet</h3>
            <p className="text-gray-500 mb-4">Create your first booking form template</p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  <Badge variant={template.is_active ? 'default' : 'secondary'}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <CardDescription className="line-clamp-2">
                  {template.description || 'No description'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-gray-500">
                  <p>Fields: {template.form_fields?.length || 0}</p>
                  <p>Signatures: {template.signature_positions?.length || 0}</p>
                  <p>PDF: {template.pdf_template_file ? 'Uploaded' : 'Not uploaded'}</p>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(template)}
                    data-testid={`edit-template-${template.id}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                    onClick={() => handleDeleteTemplate(template.id)}
                    data-testid={`delete-template-${template.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <label className="flex-1">
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleUploadPdf(template.id, e.target.files[0]);
                        }
                      }}
                    />
                    <Button variant="outline" size="sm" className="w-full" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-1" />
                        PDF
                      </span>
                    </Button>
                  </label>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={() => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-modal">
          <DialogHeader>
            <DialogTitle>
              {showEditModal ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="fields">Form Fields</TabsTrigger>
              <TabsTrigger value="signatures">Signatures</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Developer XYZ Booking Form"
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Developer ID</Label>
                  <Input
                    value={formData.developer_id}
                    onChange={(e) => setFormData({ ...formData, developer_id: e.target.value })}
                    placeholder="Optional - link to specific developer"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this template..."
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="fields" className="space-y-4">
              {/* Add Field Form */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Add New Field</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Field Name</Label>
                      <Input
                        value={newField.name}
                        onChange={(e) => setNewField({ ...newField, name: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                        placeholder="field_name"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={newField.label}
                        onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                        placeholder="Display Label"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Type</Label>
                      <Select value={newField.field_type} onValueChange={(v) => setNewField({ ...newField, field_type: v })}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FIELD_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <span className="font-mono text-xs mr-2">{type.icon}</span>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Section</Label>
                      <Select value={newField.section} onValueChange={(v) => setNewField({ ...newField, section: v })}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTIONS.map((section) => (
                            <SelectItem key={section} value={section}>{section}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {newField.field_type === 'select' && (
                    <div className="mt-3 space-y-1">
                      <Label className="text-xs">Options (comma separated)</Label>
                      <Input
                        value={newField.options}
                        onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                        placeholder="Option 1, Option 2, Option 3"
                        className="h-9"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newField.required}
                        onCheckedChange={(checked) => setNewField({ ...newField, required: checked })}
                      />
                      <Label className="text-xs">Required</Label>
                    </div>
                    <Button onClick={addFormField} size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Field
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Fields List */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Form Fields ({formData.form_fields.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {formData.form_fields.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No fields added yet</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.form_fields.map((field, index) => (
                        <div
                          key={field.id}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                        >
                          <GripVertical className="h-4 w-4 text-gray-400" />
                          <span className="text-xs font-mono bg-white px-2 py-1 rounded">
                            {FIELD_TYPES.find(t => t.value === field.field_type)?.icon}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{field.label}</p>
                            <p className="text-xs text-gray-500">{field.name} • {field.section}</p>
                          </div>
                          {field.required && (
                            <Badge variant="outline" className="text-xs">Required</Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFormField(field.id)}
                            className="text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="signatures" className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PenTool className="h-5 w-5" />
                    Signature Positions
                  </CardTitle>
                  <CardDescription>
                    Define where signatures should appear on the generated PDF
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={addSignaturePosition} variant="outline" className="mb-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Signature
                  </Button>

                  {formData.signature_positions.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No signatures defined</p>
                  ) : (
                    <div className="space-y-3">
                      {formData.signature_positions.map((sig, index) => (
                        <div key={index} className="grid grid-cols-6 gap-2 p-3 bg-gray-50 rounded-lg items-end">
                          <div className="space-y-1">
                            <Label className="text-xs">Role</Label>
                            <Select
                              value={sig.role}
                              onValueChange={(v) => updateSignaturePosition(index, 'role', v)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="customer">Customer</SelectItem>
                                <SelectItem value="agent">Agent</SelectItem>
                                <SelectItem value="witness">Witness</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Label</Label>
                            <Input
                              value={sig.label}
                              onChange={(e) => updateSignaturePosition(index, 'label', e.target.value)}
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Page</Label>
                            <Input
                              type="number"
                              value={sig.page}
                              onChange={(e) => updateSignaturePosition(index, 'page', parseInt(e.target.value))}
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">X Position</Label>
                            <Input
                              type="number"
                              value={sig.x}
                              onChange={(e) => updateSignaturePosition(index, 'x', parseFloat(e.target.value))}
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Y Position</Label>
                            <Input
                              type="number"
                              value={sig.y}
                              onChange={(e) => updateSignaturePosition(index, 'y', parseFloat(e.target.value))}
                              className="h-9"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSignaturePosition(index)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={showEditModal ? handleUpdateTemplate : handleCreateTemplate}>
              {showEditModal ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookingTemplates;
